import React, { useState } from 'react';
import { Users, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import type { Band } from '../../types/database';

interface BandSelectProps {
  bands: Band[];
  selectedBand: string | null;
  onSelectBand: (bandId: string | null) => void;
}

export function BandSelect({ bands, selectedBand, onSelectBand }: BandSelectProps) {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounce(searchQuery, 300);

  const selectedBandName = selectedBand 
    ? bands.find(b => b.id === selectedBand)?.name 
    : null;

  // Filter bands based on search query
  const filteredBands = bands.filter(band => 
    !debouncedSearch || 
    band.name.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  return (
    <div 
      className="relative"
      onBlur={(e) => {
        if (!e.relatedTarget?.closest('.band-dropdown')) {
          setShowDropdown(false);
        }
      }}
    >
      <div className="w-full md:w-64">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full px-4 py-2 bg-[#232323] border border-gray-custom/20 rounded-lg text-light/60 flex items-center justify-between hover:border-gray-custom/40 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-light/40" />
            <span className="truncate">
              {selectedBandName || t('stories.selectBand')}
            </span>
          </div>
          <ChevronDown className={`w-5 h-5 text-light/40 transition-transform ${showDropdown ? 'transform rotate-180' : ''}`} />
        </button>

        {showDropdown && (
          <div className="band-dropdown absolute z-50 w-full mt-1 bg-[#232323] border border-gray-custom/20 rounded-lg shadow-lg">
            <div className="p-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('stories.searchPlaceholder')}
                className="w-full px-3 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:outline-none focus:border-gray-custom/40"
                autoFocus
              />
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filteredBands.map((band) => (
                <button
                  key={band.id}
                  onClick={() => {
                    onSelectBand(band.id);
                    setShowDropdown(false);
                    setSearchQuery('');
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-custom/10 text-light/80 flex items-center space-x-2"
                >
                  <div className="w-8 h-8 bg-gray-custom/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {band.avatar_url ? (
                      <img
                        src={band.avatar_url}
                        alt={band.name}
                        className="w-full h-full object-cover"
                       loading="lazy" />
                    ) : (
                      <Users className="w-4 h-4 text-light/60" />
                    )}
                  </div>
                  <span className="truncate">{band.name}</span>
                </button>
              ))}
              {filteredBands.length === 0 && (
                <div className="px-4 py-2 text-light/40 text-center">
                  {t('stories.noStoriesFound')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}